import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader2, ArrowLeft, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth, ROLES } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import EdilmindLogo from "@/components/EdilmindLogo";

const ROLE_ORDER = [
  "titolare",
  "direttore_lavori",
  "responsabile_sicurezza",
  "caposquadra",
  "operaio",
];

const ROLE_DESCRIPTIONS = {
  titolare: "Accesso completo. Carica contratti, vedi tutto.",
  direttore_lavori: "Carica contratti, gestisci SAL e scadenze.",
  responsabile_sicurezza: "Sicurezza: rischi, PSC/POS, scadenze formative.",
  caposquadra: "Documenti e scadenze operative della squadra.",
  operaio: "I tuoi obblighi e cosa portare in cantiere.",
};

const colorClassByRole = {
  titolare: "bg-brand-navy hover:bg-brand-navy-2 text-white border-brand-navy",
  direttore_lavori: "bg-blue-700 hover:bg-blue-800 text-white border-blue-700",
  responsabile_sicurezza: "bg-brand-orange hover:bg-brand-orange-2 text-white border-brand-orange",
  caposquadra: "bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600",
  operaio: "bg-slate-500 hover:bg-slate-600 text-white border-slate-500",
};

const PinLogin = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState("role"); // "role" | "pin"
  const [selectedRole, setSelectedRole] = useState(null);
  const [name, setName] = useState("");
  const [pin, setPin] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleRoleClick = (roleId) => {
    setSelectedRole(roleId);
    setStep("pin");
    setPin("");
    setError("");
  };

  const handleBack = () => {
    setStep("role");
    setError("");
    setPin("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^\d{4}$/.test(pin)) {
      setError("Inserisci un PIN di 4 cifre.");
      return;
    }
    setSubmitting(true);
    try {
      const u = await login(pin, name.trim());
      if (selectedRole && u.role !== selectedRole) {
        setError(`Questo PIN corrisponde al ruolo "${ROLES[u.role]?.label}", non a "${ROLES[selectedRole]?.label}".`);
        setSubmitting(false);
        return;
      }
      toast.success(name ? `Ciao ${name.split(" ")[0]}` : "Accesso effettuato");
      const from = location.state?.from || "/";
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center px-4 py-10" data-testid="pin-login-page">
      <div className="w-full max-w-2xl">
        <div className="flex flex-col items-center justify-center gap-3 mb-6">
          <EdilmindLogo size={64} />
          <div className="text-center">
            <p className="font-display font-black tracking-tight text-2xl sm:text-3xl text-brand-navy">EDILMIND</p>
            <p className="mt-1 text-[10px] sm:text-xs font-bold tracking-[0.28em] text-brand-orange uppercase">
              Intelligenza · Ordine · Valore
            </p>
          </div>
        </div>
        <h1 className="text-center font-display text-2xl sm:text-3xl font-black tracking-tight text-brand-navy" data-testid="pin-login-title">
          Chi sei oggi in cantiere?
        </h1>
        <p className="mt-2 text-center text-sm text-slate-500">
          Seleziona il tuo ruolo per entrare. Ognuno vede solo ciò che gli serve.
        </p>

        {step === "role" ? (
          <div className="mt-8 grid sm:grid-cols-2 gap-3" data-testid="role-grid">
            {ROLE_ORDER.map((roleId) => (
              <button
                key={roleId}
                type="button"
                data-testid={`role-btn-${roleId}`}
                onClick={() => handleRoleClick(roleId)}
                className={`text-left p-5 rounded-xl border-2 transition-all hover:-translate-y-0.5 ${colorClassByRole[roleId]}`}
              >
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] opacity-80">
                  Ruolo
                </div>
                <div className="mt-1 font-display text-xl font-bold leading-tight">
                  {ROLES[roleId].label}
                </div>
                <p className="mt-2 text-xs opacity-90 leading-relaxed">
                  {ROLE_DESCRIPTIONS[roleId]}
                </p>
              </button>
            ))}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-8 bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-sm space-y-5" data-testid="pin-step">
            <button
              type="button"
              onClick={handleBack}
              data-testid="pin-back-btn"
              className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900"
            >
              <ArrowLeft size={13} /> Cambia ruolo
            </button>

            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colorClassByRole[selectedRole]}`}>
                <KeyRound size={20} />
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Ruolo selezionato</p>
                <p className="font-display text-lg font-bold" data-testid="pin-selected-role">
                  {ROLES[selectedRole].label}
                </p>
              </div>
            </div>

            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                Nome (opzionale)
              </span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Es. Marco"
                data-testid="pin-name-input"
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                autoComplete="name"
              />
            </label>

            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                PIN a 4 cifre
              </span>
              <input
                type="password"
                inputMode="numeric"
                pattern="\d{4}"
                maxLength={4}
                required
                autoFocus
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                data-testid="pin-input"
                className="mt-1 w-full rounded-md border-2 border-slate-300 px-3 py-3 text-2xl font-mono font-bold tracking-[0.5em] text-center focus:outline-none focus:ring-2 focus:ring-emerald-500"
                autoComplete="off"
              />
            </label>

            {error && (
              <div data-testid="pin-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || pin.length !== 4}
              data-testid="pin-submit-btn"
              className="w-full inline-flex items-center justify-center gap-2 bg-brand-navy hover:bg-brand-navy-2 text-white font-bold py-3 rounded-md transition-colors disabled:opacity-60"
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />}
              Accedi
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default PinLogin;
